import React, { useEffect, useRef, useState } from 'react';
import { getFramework, useGlobalMask, useIdentity, useScale } from '@/framework';
import type { EdgelessMainLayer } from '@/layers/main-layer/edgeless-layer';
import { isLeftButton } from '@/utils/event-kit';
import type { ILayerProps, IPoint } from '@/version-types';
import { convertMousePointToLayerPoint } from '../../../../../utils';
import type { IntelligentColorPlugin } from '../controller';
import { useIntelligentColorState } from '../states/intelligent-color-state';
import './color-pick-pen.less';

// export const MAX_COLOR_LENGTH = 6;

export const ColorPickPen = React.memo(() => {
    const DEFAULT_COLOR = '0,0,0,1';
    const identity = useIdentity();
    const framework = getFramework(identity);
    // const originIndex = framework.service.frame.getActiveIndex();

    const scale = useScale();
    const globalMask = useGlobalMask();
    const isShowLoading = globalMask !== null;

    const penSize = useIntelligentColorState()((state) => state.intelligentPenSize);
    const replaceIntelligentPickColor = useIntelligentColorState()((state) => state.replaceIntelligentPickColor);
    const intelligentController =
        framework.service.registration.getInstance<IntelligentColorPlugin>('IntelligentColorPlugin');
    const intelligentColors = useIntelligentColorState()((state) => state.intelligentColors);
    const setSelectedData = useIntelligentColorState()((state) => state.setSelectedData);
    const setIsOpenIntelligentPickColor = useIntelligentColorState()((state) => state.setIsOpenIntelligentPickColor);

    // 绘制时候的画笔大小
    const drawPenSize = 15 / scale;
    const [mousePosition, setMousePosition] = useState<IPoint>({ x: -10000, y: 0 });
    const [color, setColor] = useState(DEFAULT_COLOR);
    const [inStage, setInStage] = useState<boolean>(false);
    const viewportPoint = inStage ? framework.service.transform.getViewportPoint(mousePosition) : mousePosition;
    const colorPickData = useRef<{ canvas: HTMLCanvasElement | null; layer: ILayerProps }>();

    useEffect(() => {
        const getImageData = async (position: IPoint) => {
            if (!colorPickData.current?.canvas) {
                const frameId = framework.service.frame.getActiveFrameId();
                const mainLayer = framework.service.frame.getMainLayer(frameId);
                if (mainLayer) {
                    const obj = framework.service.edgelessCanvas.getByLayerId(mainLayer.id) as
                        | EdgelessMainLayer
                        | undefined;
                    if (obj) {
                        colorPickData.current = {
                            canvas: await obj.getCanvasResult({ forceRerender: true }),
                            layer: mainLayer,
                        };
                    }
                }
            }
            if (colorPickData.current?.canvas) {
                const { layer } = colorPickData.current;
                // 取色画布使用图层局部坐标，先移除长图页面偏移，再处理旋转和翻转。
                const newPoint = convertMousePointToLayerPoint(layer, {
                    x: position.x,
                    y: position.y - framework.service.transform.calcLayerOffsetTop(layer),
                });
                const canvas = colorPickData.current.canvas;
                const ctx = canvas.getContext('2d');
                const imagedata = ctx?.getImageData(newPoint.x, newPoint.y, 1, 1) as any;
                if (imagedata?.data) {
                    const rgba = Array.from(imagedata?.data).join(',');
                    return rgba;
                }
            }
            return '';
        };

        const downHandle = async (e) => {
            if (!isLeftButton(e)) return;
            if (isShowLoading) return;
            const mPoint = { x: e.clientX, y: e.clientY };
            const scenePoint = framework.service.transform.getScenePoint(e);
            const position = scenePoint;
            const activeStageIndex = framework.service.frame.getActiveIndex();
            const inRect = framework.service.transform.checkPointInFrame(mPoint, activeStageIndex);
            if (inRect) {
                const color = await getImageData(position);
                setColor(color);
                const allColors = intelligentColors.map((v) => v.color);
                const index = allColors.findIndex((v) => v === color);
                if (index > -1) {
                    setSelectedData({ index, ...intelligentColors[index] });
                } else {
                    replaceIntelligentPickColor(color);
                }
                intelligentController.renderIntelligentPreview(color);
            }
            setIsOpenIntelligentPickColor(false);
            colorPickData.current = undefined;
        };
        const moveHandle = async (e) => {
            // 设置鼠标位置
            const mPoint = { x: e.clientX, y: e.clientY };
            const scenePoint = framework.service.transform.getScenePoint(e);
            const position = scenePoint;
            const activeStageIndex = framework.service.frame.getActiveIndex();
            const inRect = framework.service.transform.checkPointInFrame(mPoint, activeStageIndex);
            setMousePosition(mPoint);
            if (inRect) {
                const color = (await getImageData(position)) as any;
                setColor(color);
                // setInStage(true);
            } else {
                // setInStage(false);
            }
            setInStage(inRect);
        };

        document.addEventListener('mousemove', moveHandle);
        document.addEventListener('mousedown', downHandle);
        return () => {
            document.removeEventListener('mousemove', moveHandle);
            document.removeEventListener('mousedown', downHandle);
        };
    }, [isShowLoading, drawPenSize, scale, intelligentColors]);

    return inStage ? (
        <div
            style={{
                width: penSize,
                height: penSize,
                left: 0,
                top: 0,
                transform: `translate(${viewportPoint.x}px, ${viewportPoint.y}px) translate(-50%, -50%)`,
            }}
            className="m-color-pick-pen"
        >
            <div className="m-color-pick-pen__color" style={{ borderColor: `rgba(${color})` }}></div>
        </div>
    ) : null;
});
